import { Inject, Injectable } from "@angular/core";
import { BaseService } from "../../../services/base.service";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { ITableState, TableResponseModel } from "../../../services/crud-table/models/table.model";
import { IOrderModel } from "../../../models/order.model";
import { BehaviorSubject, Observable } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { IBuyerOrder } from "../models/buyer-order.model";

export interface ICreateOrderProduct {
	id: string;
	volume: number;
	isContractPrice: boolean;
	contractId: string;
	requestedProductId?: string;
}
export interface ICreateOrder {
	products: ICreateOrderProduct[];
	deliveryAddress: string[];
	orderType: string;
	orderAmount: number;
	currency: string;
	paymentType: string;
	deliveryDate: Date;
	postalAddress?: string[];
	email?: string;
	isDraft?: boolean;
}

@Injectable({
	providedIn: "root",
})
export class BuyerOrderService<T> extends BaseService<T> {
	currentLanguage: string;
	private _allOrder$ = new BehaviorSubject([]);
	get allOrder$() {
		return this._allOrder$.asObservable();
	}

	private _ordersCount$: BehaviorSubject<number> = new BehaviorSubject(0);
    get ordersCount$() {
        return this._ordersCount$.asObservable();
    }

	private _order$ : BehaviorSubject<any>  = new BehaviorSubject({});

	get order$() {
		return this._order$.asObservable();
	}

	private baseURL: string =
		environment.API_ENDPOINT +
		environment.VERSION_V1 +
		environment.API_PREFIX_BUYER;

	constructor(
		private translateService: TranslateService,
		@Inject(HttpClient) http
	) {
		super(http);
		this.currentLanguage = this.translateService.currentLang;
		this.translateService.onLangChange.subscribe(response => {
			this.currentLanguage = response.lang;
		});
	}

	/**
	 * Handle create order functionality
	 * @param orderData : ICreateOrder product's data
	 * @returns
	 */
	placeOrder(orderData): Observable<any> {
		const url = this.baseURL + environment.API_ROUTES.ORDERS.CREATE;
		orderData.isDraft = false;
		this._isLoading$.next(true);
		return this.create(orderData, url);
	}

	/**
	 * Handle create/update draft order functionality
	 * @param orderData : ICreateOrder order product's data
	 * @returns
	 */
	placeDraftOrder(orderData): Observable<any> {
		const url = this.baseURL + environment.API_ROUTES.ORDERS.CREATE_DRAFT;
		return this.create(orderData, url);
	}

	filterOrders(tableState: Partial<ITableState>): void {
		const endpoint = this.baseURL + environment.API_ROUTES.ORDERS.FIND;
		this.patchState(endpoint, tableState).subscribe((res:TableResponseModel<T>) => {
			this._allOrder$.next(res.items);
			this._ordersCount$.next(res.total);
		});
	}

	getOrder(id: string): void {
		const endpoint = this.baseURL + environment.API_ROUTES.ORDERS.GET + '/find/' + id;
		this.getById(endpoint, id).subscribe((res) => {
			this._order$.next(res);
		});
	}

	deleteOrder(id: string): void {
		const endpoint = this.baseURL + environment.API_ROUTES.ORDERS.DELETE;
		this.delete(endpoint, [id]);
	}

	deleteOrders(ids: string[]): void {
		const endpoint = this.baseURL + environment.API_ROUTES.ORDERS.DELETE;
		this.delete(endpoint, ids);
	}

	changeOrderStatus(
		order: IOrderModel,
		statusPayload: {status: string, reason?: string}
	): Observable<any> {
		const endpoint = this.baseURL + environment.API_ROUTES.ORDERS.CHANGE_STATUS + "/" + order._id
			+ this.API_ROUTES.ORDERS.STATUS;
		return this.put(endpoint, statusPayload);
	}

	/**
	 * Get all categories
	 */
	fetchCategories(sorting): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.CATEGORY.SEARCH;
		return this.findItemCategories(url, { paginator: {}, searchTerm: "", sorting }, this.currentLanguage);
	}

	/**
	 * Download list of products
	 */
	downloadMyProducts(orderId: string, fileType: string) {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.ORDERS.EXPORT
			+ `/${orderId}?fileType=${fileType}`;
		this._isLoading$.next(true);
		return this.downloadItems(url, null, this.currentLanguage);
	}

	/**
	 * delete draft orders
	 */
	deleteDraftOrders(data: object): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.ORDERS.DELETE;
		return this.deleteItems(data, url);
	}

	/**
	 * Find product details
	 */
	getProductDetails(orderId: string, productId: string): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.PRODUCTS.SEARCH
			+ `/${productId}` + this.API_ROUTES.COMMON.DETAILS + `/${orderId}?detailsType=order`;
		return this.getDetails(url);
	}

	/**
	 * Get all orders
	 */
	fetchAllOrders(tableState): Observable<any> {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.ORDERS.GET;
		return this.findItems(url, tableState);
	}

	getAllOrderSuppliers() {
		const url = this.API_URL + this.VERSION_V1 + this.API_ROUTES.BUYER.ORDERS.ALL_SUPPLIERS;
		return this.http.get(url);
	}
}
